import './OffersSearchFilters.css';

import React, { RefObject, useCallback } from 'react';

import { Button } from '@vtblife/uikit';

import classname from '@search/classname/src';
import { formatNumber } from '@search/helpers/src/formatNumber';
import { ClassifiedFilterCollection } from '@search/vtbeco-frontend-core/domain/offer/filters/ClassifiedFilterCollection';
import { IFilter } from '@search/vtbeco-frontend-core/view/filters/models/Filter';
import { GeoBaseStore } from '@search/vtbeco-frontend-core/domain/geo/GeoBase';
import { AddQueryDisplayValue, QueryDisplayValues } from '@search/vtbeco-frontend-core/view/common/hooks/useQueryDisplayValues';
import { TipItem, OffersSearchTips, getTipsCollection } from '@search/vtbeco-frontend-core/view/tips/OffersSearchTips';
import { LastSearch } from '@search/classified-common/src/LastSearch/LastSearch';

import { IRegion } from '@search/geo/src/geoRootRegionFallback';
import { OffersSearchFiltersMoreButton } from './OffersSearchFiltersMoreButton/OffersSearchFiltersMoreButton';
import { OfferSearchFiltersMainView } from './mainView/OfferSearchFiltersMainView';

export const cn = classname.bind(null, 'OffersSearchFilters');

export const OffersSearchFilters = ({
    geoStore,
    removeFilter,
    removeGeo,
    handleResetGeo,
    handleReset,
    filters,
    updateFilter,
    updateFewFilters,
    openExtraFilter,
    closeExtraVisible,
    extraFilterVisible,
    handleSubmit,
    count,
    loading,
    tipsVisible,
    setTipsVisible,
    setTipsExpanded,
    tipsExpanded,
    queryDisplayValues,
    addQueryDisplayValue,
    handleMapSearch,
    onSaveSearch,
    serpContentRef,
    lastSearches,
    showPromoRieltors,
    openMortgagePaymentSearchModal,
    regionId,
    rootRegion
}: {
    loading: boolean;
    setTipsVisible: (visible: boolean) => void;
    tipsVisible: boolean;
    setTipsExpanded: (expanded: boolean) => void;
    tipsExpanded: boolean;
    geoStore: GeoBaseStore;
    removeFilter: (filter: IFilter) => void;
    removeGeo: (tip: TipItem[]) => void;
    handleResetGeo: () => void;
    handleReset: () => void;
    filters: ClassifiedFilterCollection;
    updateFilter: (filter: IFilter) => void;
    updateFewFilters: (filter: IFilter[]) => void;
    openExtraFilter: () => void;
    closeExtraVisible: () => void;
    extraFilterVisible: boolean;
    handleSubmit: (needClear?: boolean) => void;
    count: number;
    queryDisplayValues: QueryDisplayValues;
    addQueryDisplayValue: AddQueryDisplayValue;
    handleMapSearch: () => void;
    onSaveSearch: () => void;
    serpContentRef: RefObject<HTMLElement | null>;
    lastSearches: {
        searchText: string;
        searchUrl: string;
    }[];
    showPromoRieltors: boolean;
    openMortgagePaymentSearchModal?: () => void;
    regionId: number;
    rootRegion?: IRegion;
}) => {
    const tipsChanged = useCallback((length: number) => {
        setTipsVisible(length > 0);
    }, [ setTipsVisible ]);

    const submitAndClear = useCallback(() => {
        handleSubmit(true);
    }, [ handleSubmit ]);

    const submitOnly = useCallback(() => {
        handleSubmit(false);
    }, [ handleSubmit ]);

    // eslint-disable-next-line no-nested-ternary
    const submitedText = loading ? 'Показать' : (
        count > 0 ?
            `Показать ${formatNumber(count)}` :
            'Нет объявлений'
    );

    const tips = getTipsCollection({
        queryDisplayValues,
        geoStore,
        filters
    });

    const renderLastSearches = lastSearches.map(search => <LastSearch key={search.searchUrl} {...search} />);

    return (
        <OfferSearchFiltersMainView
            serpContentRef={serpContentRef}
            onSaveSearch={onSaveSearch}
            count={count}
            filters={filters}
            geoStore={geoStore}
            updateFilter={updateFilter}
            updateFewFilters={updateFewFilters}
            addQueryDisplayValue={addQueryDisplayValue}
            setTipsExpanded={setTipsExpanded}
            more={
                <OffersSearchFiltersMoreButton
                    filters={filters}
                    count={count}
                    openExtraFilter={openExtraFilter}
                    updateFewFilters={updateFewFilters}
                    closeExtraVisible={closeExtraVisible}
                    extraFilterVisible={extraFilterVisible}
                    updateFilter={updateFilter}
                    handleSubmit={submitOnly}
                    handleReset={handleReset}
                    handleMapSearch={handleMapSearch}
                    tipsAnalytics={tips.tipsCollection}
                    showPromoRieltors={showPromoRieltors}
                />
            }
            buttonSubmit={
                <Button
                    dataTest='filter-submit'
                    onClick={submitAndClear}
                    width={200}
                >
                    {submitedText}
                </Button>
            }
            lastSearches={renderLastSearches}
            tips={
                <div className={cn('tips-position', { expanded: tipsVisible })}>
                    <div className={cn('tips-popup', { expanded: tipsExpanded })}>
                        <div className={cn('tips-limiter')}>
                            <OffersSearchTips
                                tipsExpanded={tipsExpanded}
                                classNamesNewFilters={{
                                    firstLineTipsOnly: cn('first-line-tips-only-new-filters'),
                                    allTips: cn('all-tips-new-filters'),
                                    tipsButtons: cn('tips-buttons-new-filters')
                                }}
                                removeFilter={removeFilter}
                                updateFilter={updateFilter}
                                expandFilter={setTipsExpanded}
                                filters={filters}
                                loading={loading}
                                removeGeo={removeGeo}
                                onUpdateLength={tipsChanged}
                                reset={handleResetGeo}
                                tips={tips}
                                openMortgagePaymentSearchModal={openMortgagePaymentSearchModal}
                            />
                        </div>
                    </div>
                </div>
            }
            regionId={regionId}
            rootRegion={rootRegion}
        />
    );
};
